import api from '../api';

/**
 * Attachment-question file service.
 *
 * Backend endpoints used:
 *   POST   /questionnaires/:id/upload           -> { id }        (upload)
 *   GET    /files/:id?open=true                 -> file stream   (preview/download)
 *   DELETE /questionnaires/:id/upload/:fileId   -> questionnaire (delete)
 *
 * `:id` is the real questionnaire id in every case. The delete endpoint
 * enforces that the file's stored metadata.questionnaire_id matches, so the
 * upload MUST run with the questionnaire id (not the question id).
 *
 * The server does not expose file metadata (filename/size/content type), so we
 * capture it from the local File at upload time and keep it in sessionStorage
 * for the lifetime of the tab (survives reloads; the raw bytes are re-fetched
 * from GET /files/:id on demand). If the cache is gone (new session/browser),
 * the card falls back to a generic label and preview still works via the blob.
 */

// mirrors the multer fileSize limit on POST /questionnaires/:id/upload
export const MAX_FILE_SIZE = 10 * 1024 * 1024;

const META_STORAGE_KEY = 'legalup_attachment_meta_v1';

const blobCache = new Map();

const readStoredMeta = () => {
    try {
        return JSON.parse(sessionStorage.getItem(META_STORAGE_KEY)) || {};
    } catch (err) {
        return {};
    }
};

const writeStoredMeta = all => {
    try {
        sessionStorage.setItem(META_STORAGE_KEY, JSON.stringify(all));
    } catch (err) {
        // storage full / unavailable — the in-tab flow still works via blobCache
    }
};

const cacheMeta = meta => {
    const all = readStoredMeta();
    all[meta.id] = meta;
    writeStoredMeta(all);
};

const removeCachedMeta = fileId => {
    const all = readStoredMeta();
    delete all[fileId];
    writeStoredMeta(all);
};

export const getCachedMeta = fileId => readStoredMeta()[fileId] || null;

export async function uploadAttachment(questionnaireId, file) {
    if (file.size > MAX_FILE_SIZE) {
        const error = new Error('File exceeds the size limit');
        error.code = 'FILE_TOO_LARGE';
        throw error;
    }
    const res = await api.questionnaires.uploadFile(questionnaireId, file);
    // today res is only { id }; keep richer server fields if they appear later
    const meta = {
        filename: file.name,
        size: file.size,
        content_type: file.type || '',
        ...res
    };
    blobCache.set(meta.id, file);
    cacheMeta(meta);
    return meta;
}

export async function deleteAttachment(questionnaireId, fileId) {
    await api.questionnaires.deleteUpload(questionnaireId, fileId);
    blobCache.delete(fileId);
    removeCachedMeta(fileId);
}

export function getAttachmentMeta(fileId) {
    // No server metadata endpoint — the sessionStorage cache is the only source.
    return getCachedMeta(fileId);
}

export async function getAttachmentBlob(fileId) {
    const cached = blobCache.get(fileId);
    if (cached) {
        return cached;
    }
    const blob = await api.files.getBlob(fileId);
    blobCache.set(fileId, blob);
    if (!getCachedMeta(fileId)) {
        cacheMeta({ id: fileId, filename: '', size: blob.size, content_type: blob.type || '' });
    }
    return blob;
}

const DOCX_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const DOC_TYPE = 'application/msword';
const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'];

const extensionOf = filename => {
    const name = (filename || '').toLowerCase();
    const dot = name.lastIndexOf('.');
    return dot >= 0 ? name.slice(dot + 1) : '';
};

// -> 'image' | 'pdf' | 'docx' | 'doc' | 'other'
export function detectPreviewKind({ content_type, filename } = {}) {
    const type = (content_type || '').toLowerCase();
    if (type.indexOf('image/') === 0) return 'image';
    if (type === 'application/pdf') return 'pdf';
    if (type === DOCX_TYPE) return 'docx';
    if (type === DOC_TYPE) return 'doc';

    const ext = extensionOf(filename);
    if (IMAGE_EXTENSIONS.indexOf(ext) >= 0) return 'image';
    if (ext === 'pdf') return 'pdf';
    if (ext === 'docx') return 'docx';
    if (ext === 'doc') return 'doc';
    return 'other';
}

export function formatFileSize(bytes) {
    if (typeof bytes !== 'number' || isNaN(bytes) || bytes < 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
